import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as actions from '../../actions/contracts/review';
import * as selectors from '../../selectors/contracts/review';
import ReviewHeader from '../../components/application/contracts/ReviewHeader';
import { searchNodeNameUp } from '../../lib/dom.utils';
import ReviewComment from '../../components/application/contracts/ReviewComment';
import ReviewFooter from '../../components/application/contracts/ReviewFooter';
import { HTML_CLASSES } from '../../constants/admin/settings';
import { currentUserSelector } from '../../selectors/authentication';
import { hasPermissions, PERMISSIONS } from '../../constants/authentication/permissions';
import Loading from '../../components/helpers/Loading';
import { Prompt } from 'react-router';
import { ARE_YOU_SURE_TEXT } from '../../constants/texts/errors';
import { cleanRichTextHtml } from '../../lib/html.utils';
import CKPButton from '../../components/forms/Button';
import { downloadUploadedContract, downloadContract } from '../../actions/admin/models/contracts';
import { forEach, get } from 'lodash';
import { REVIEW_PATH } from '../../constants/routes';
import ReviewCodeModal from '../../components/application/contracts/ReviewCodeModal';
import { CONTRACT_STATE_SENT, CONTRACT_TYPES } from '../../constants/settings';
import SelectField from '../../components/forms/SelectField';
import { activitiesBuilder, versionsToDropdownItems } from '../../selectors/admin/models/contracts';
import RTEWithComments from '../../components/admin/hocs/RTEWithComments';
import Activity from '../../components/admin/forms/contracts/Activity';

const STOP_CLICK_CLASS = 'js-contracts-review-click';
const STOP_CLICK_NODE = 'LOCK';
const CHECKBOX_NODE = 'CB';
const INPUT_NODE = 'INPUT';
const NO_COMMENTS_NODES = [CHECKBOX_NODE, INPUT_NODE];
const HIGHLIGHT_NODES = ['B', 'I', 'A'];

class Review extends Component {
    state = {
        shouldBlockNavigation: false,
        authenticated: false,
        pinError: '',
        savedComments: false,
        savedText: false
    };

    constructor(props) {
        super(props);
        this.contractTextRef = React.createRef();
    }

    componentDidMount() {
        this.props.getMember(this.props.match.params.id, this._isPublic(), !this.state.authenticated);
    }

    componentDidUpdate() {
        if (
            (this.props.hasComments && !this.props.isSigned && !this.props.isSubmitting) !==
            this.state.shouldBlockNavigation
        ) {
            this.setState({
                shouldBlockNavigation: this.props.hasComments && !this.props.isSigned && !this.props.isSubmitting
            });
        }

        cleanRichTextHtml('ckp-contracts-review-text');
    }

    _isPublic = () => get(this, 'props.match.path') === REVIEW_PATH;

    _isReviewable = () => selectors.isReviewableState(this.props.contractState, this.props.contract, this._isPublic());

    _onTextClick = e => {
        return searchNodeNameUp(e, this._specialBehavior, this._openCommentModal, {
            search: STOP_CLICK_NODE,
            exceptions: NO_COMMENTS_NODES,
            border: STOP_CLICK_CLASS
        });
    };

    _specialBehavior = e => {
        if (!this.props.isSigned) {
            const target = e.target;
            switch (target.nodeName) {
                // TODO-NT Backward compatibility, delete when confident
                case CHECKBOX_NODE:
                    if (
                        hasPermissions(this.props.currentUser, PERMISSIONS.SIGN_CONTRACTS_INBOX) &&
                        this._isReviewable()
                    ) {
                        target.innerHTML = target.classList.contains('is-checked') ? '&#9744;' : '&#9745;';
                        return target.classList.toggle('is-checked');
                    }
                    return true;

                case INPUT_NODE:
                    if (
                        hasPermissions(this.props.currentUser, PERMISSIONS.SIGN_CONTRACTS_INBOX) &&
                        this._isReviewable() &&
                        target.type === 'checkbox'
                    ) {
                        if (target.checked) {
                            return target.setAttribute('checked', target.checked);
                        }
                        return target.removeAttribute('checked');
                    }
                default:
                    break;
            }
        }
    };

    _openCommentModal = e => {
        if (
            (hasPermissions(this.props.currentUser, PERMISSIONS.COMMENT_CONTRACTS_INBOX) || this._isPublic()) &&
            this._isReviewable()
        ) {
            let target = e.target;
            while (HIGHLIGHT_NODES.includes(target.nodeName)) {
                target = target.parentElement;
            }
            if (!this._isReviewable() && target.id && this.props.allComments[target.id]) {
                this.props.openComments(target);
            } else if (this._isReviewable()) {
                target.id = target.id || `js-id-${Date.now()}`;
                this.props.openComments(target);
            }
        }
    };

    _commentAdd = (elementId, value) => {
        if (value) {
            this.props.signed && this.props.toggleSign();
            this.props.nameInput('');
            this.props.functionInput('');
        }
        this.props.commenting.classList[value ? 'add' : 'remove'](HTML_CLASSES.RTE_COMMENTS);
        this.props.commenting.classList[value ? 'add' : 'remove'](HTML_CLASSES.RTE_JS_COMMENTS);
        this.props.commentAdd(elementId, value);
    };

    _submitAsDraft = () => {
        window.scrollTo(0, 0);
        this._submit(true);
    };

    _submitToReview = () => {
        window.scrollTo(0, 0);
        this._submit(false);
    };

    _submit = (asDraft = false) => {
        this.props.startSubmit();
        const transformedHtml = selectors.contractTextFeedback(
            this.contractTextRef.current,
            this.props.signed,
            this.props.contract,
            this.props.signName,
            this.props.signFunction
        );
        this.contractTextRef.current.innerHTML = transformedHtml;
        if (this._isPublic()) {
            this.props.submitPublic(transformedHtml, this.state.authenticated);
        } else {
            this.props.submit(transformedHtml, asDraft);
        }
    };

    _download = () => {
        this.props.downloadContract(this.props.contract.id, get(this.props.contract, 'version.id'), this._isPublic());
    };

    _downloadUploadedVersion = () => {
        this.props.downloadUploadedContract(this.props.contract.id, get(this.props.contract, 'version.id'));
    };

    _submitPinCode = async value => {
        await this.props.getMember(this.props.match.params.id, this._isPublic(), false, value);
        if (this.props.contract.pinError) {
            return this.setState({ authenticated: false, pinError: 'Invalid PIN' });
        }
        if (this.props.contract.id) {
            return this.setState({ authenticated: value, pinError: '' });
        }
    };

    _switchVersion = val => {
        const htmlText = document.getElementById('ckp-contracts-review-text');
        if (
            val !== this.props.contract.versions[0].id &&
            this.props.contractState === CONTRACT_STATE_SENT &&
            !this.state.savedComments
        ) {
            this.setState({
                savedComments: this.props.allComments,
                savedText: (htmlText && htmlText.innerHTML) || ''
            });
            forEach(this.props.allComments, (_v, key) => {
                const elem = document.getElementById(key);
                if (elem) {
                    elem.classList.remove(HTML_CLASSES.RTE_COMMENTS);
                }
            });
            return this.props.switchVersion(val, false);
        } else if (this.state.savedComments && val === this.props.contract.versions[0].id) {
            this.props.switchVersion(val, this.state.savedComments);
            const savedText = this.state.savedText;
            const setBackComments = () => {
                if (htmlText) {
                    htmlText.innerHTML = savedText;
                }
                forEach(this.state.savedComments, (_v, key) => {
                    const elem = document.getElementById(key);
                    if (elem) {
                        elem.classList.add(HTML_CLASSES.RTE_COMMENTS);
                    }
                });
            };
            setTimeout(setBackComments, 100);
            setTimeout(setBackComments, 200);
            setTimeout(setBackComments, 400);
            return this.setState({ savedComments: false, savedText: false });
        } else {
            return this.props.switchVersion(val, false);
        }
    };

    render() {
        const currentIsLast = selectors.currentVersionIsLastVersion(this.props.contract, this._isPublic());
        const showContract =
            !this._isPublic() || this.state.authenticated || this.props.contract.type === CONTRACT_TYPES.CLEARANCE;
        return (
            <React.Fragment>
                <ReviewCodeModal
                    open={
                        !this.state.authenticated &&
                        this.props.contract.type === CONTRACT_TYPES.PERFORMANCE &&
                        this._isPublic()
                    }
                    confirmAction={this._submitPinCode}
                    error={this.state.pinError}
                />
                {showContract ? (
                    <div
                        className={`ckp-contracts-review ${this._isPublic() ? 'ckp-contracts-review__public' : ''} ${
                            this.props.isSubmitting ? 'is-submitting' : ''
                        } `}
                    >
                        <Prompt when={this.state.shouldBlockNavigation} message={ARE_YOU_SURE_TEXT} />
                        <ReviewComment
                            disabled={!this._isReviewable()}
                            addComment={this._commentAdd}
                            onComment={this.props.commentInput}
                            elementId={this.props.commenting && this.props.commenting.id}
                            comment={this.props.currentComment}
                            savedComment={this.props.savedComment}
                            isOpen={this.props.commenting}
                            close={this.props.closeComments}
                        />
                        <h1 className="ckp-ts-h1 u-text-center ckp-contracts-review-title">Review Contract</h1>
                        {this.props.isFetching ? <Loading label={'Loading contract...'} className={'is-big'} /> : null}
                        {this.props.fetched && this.props.contract.id ? (
                            <div className={`ckp-contracts-review-page`}>
                                {this.props.isSubmitting ? (
                                    <Loading label={this.props.submitMessage} className={'is-big'} />
                                ) : null}
                                <ReviewHeader
                                    currentIsLast={currentIsLast}
                                    isUpload={this.props.isUpload}
                                    userName={this.props.userName}
                                    signedOn={this.props.signedOn}
                                    state={this.props.contractState}
                                    comments={this.props.allComments}
                                />
                                <div className="ckp-contracts-review__versions">
                                    <div className="ckp-contracts-review__switch">
                                        <SelectField
                                            onChange={this._switchVersion}
                                            items={versionsToDropdownItems(this.props.contract.versions)}
                                            label="Contract version"
                                            className="is-without-padding"
                                            value={get(this.props, 'contract.version.id')}
                                        />
                                    </div>
                                </div>
                                <div className="u-margin-bottom-2 u-margin-top-2 u-text-center">
                                    {currentIsLast ? (
                                        <CKPButton
                                            onClick={
                                                this.props.isUpload ? this._downloadUploadedVersion : this._download
                                            }
                                            label="Download contract"
                                        />
                                    ) : null}
                                </div>
                                {get(this.props, 'contract.version.logo') &&
                                get(this.props, 'contract.version.logo.data.length') !== 0 ? (
                                    <img
                                        alt="Contract logo"
                                        className="ckp-contracts-review-logo"
                                        src={get(this.props, 'contract.version.logo')}
                                    />
                                ) : (
                                    ''
                                )}
                                <div className="ckp-contracts-review__main">
                                    <div className="ckp-contracts-review__left">
                                        <RTEWithComments
                                            data={this.props.contract.version.data}
                                            comments={this.props.allComments}
                                            editing={currentIsLast && this.props.contractState === CONTRACT_STATE_SENT}
                                            user={this.props.versionUser}
                                            version={this.props.contract.version.id}
                                        >
                                            <div
                                                id="ckp-contracts-review-text"
                                                ref={this.contractTextRef}
                                                onClick={this._onTextClick}
                                                className={`ckp-contracts-review-text ckp-rich-text-content is-${this.props.contractState} ${STOP_CLICK_CLASS}`}
                                                dangerouslySetInnerHTML={this.props.contractText}
                                            />
                                        </RTEWithComments>
                                    </div>
                                    <div className="ckp-contracts-review__right">
                                        <Activity
                                            contractId={this.props.contract.id}
                                            activity={activitiesBuilder(
                                                this.props.contract.versions,
                                                this.props.contract.reminders
                                            )}
                                        />
                                    </div>
                                </div>
                                {this._isReviewable() ? (
                                    <ReviewFooter
                                        loggedIn={!!this.props.currentUser.id}
                                        saveAsDraft={this._submitAsDraft}
                                        submit={this._submitToReview}
                                        toggleSign={this.props.toggleSign}
                                        canSubmit={this.props.canSubmit}
                                        isPublic={this._isPublic()}
                                        signed={this.props.signed}
                                        signName={this.props.signName}
                                        signEmail={this.props.signEmail}
                                        signFunction={this.props.signFunction}
                                        onFunctionChange={this.props.functionInput}
                                        onNameChange={this.props.nameInput}
                                        onEmailChange={this.props.emailInput}
                                        hasComments={this.props.hasComments}
                                    />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </React.Fragment>
        );
    }
}

const mapStateToProps = state => ({
    currentUser: currentUserSelector(state),
    hasComments: selectors.hasComments(state),
    submitMessage: selectors.submitMessageSelector(state),
    allComments: selectors.commentsSelector(state),
    currentComment: selectors.currentCommentSelector(state),
    savedComment: selectors.savedCommentSelector(state),
    isFetching: selectors.fetchingSelector(state),
    fetched: selectors.fetchedSelector(state),
    contract: selectors.contractSelector(state),
    commenting: selectors.commentingSelector(state),
    signed: selectors.signedSelector(state),
    isSigned: selectors.isSignedSelector(state),
    versionUser: selectors.getUserFromVersion(state),
    isUpload: selectors.isUploadSelector(state),
    contractState: selectors.stateSelector(state),
    signName: selectors.signNameSelector(state),
    signFunction: selectors.signFunctionSelector(state),
    signEmail: selectors.signEmailSelector(state),
    canSubmit: selectors.canSubmitSelector(state),
    isSubmitting: selectors.submittingSelector(state),
    userName: selectors.userNameSelector(state),
    signedOn: selectors.signedOnSelector(state),
    contractText: { __html: selectors.contractTextSelector(state) }
});

const mapDispatchToProps = dispatch =>
    bindActionCreators({ ...actions, downloadUploadedContract, downloadContract }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Review);



// WEBPACK FOOTER //
// ./src/containers/contracts/Review.js